import { clearAcquisitionTouch } from './acquisition-touch'
import type { PostHog, PostHogConfig } from 'posthog-js'
import { safeGetItem, safeRemoveItem, safeSetItem } from '@/lib/utils/safe-storage'
import type { AccountAnalyticsConsent } from '@/lib/types/consent'

/*
 * Analytics consent: the visitor's answer and what PostHog may do with it.
 *
 * Two modes, nothing in between:
 *   - `anonymous` (no answer yet, rejected, closed the banner, the app
 *     without an account yes):
 *     PostHog keeps everything in memory (no cookie, no localStorage, no
 *     sessionStorage), records no sessions, autocaptures no clicks and never
 *     identifies anyone. Pageviews and our own events still arrive, each page
 *     load with a fresh random id, so the baseline counts keep working.
 *   - `full` (a yes): persistent storage, the signed-in user is identified
 *     (`<PostHogIdentify>`), autocapture on and session recording as
 *     configured in PostHog.
 *
 * Whose yes (`resolveAnalyticsMode`): signed out, the web cookie banner's
 * answer on this browser; signed in, the account's answer (Settings →
 * Privacy & Consent, `consent_records`), on the web and in the app. The
 * banner's answer lives in localStorage under our own keys (written by
 * `hooks/use-cookie-consent.ts`), with an in-memory fallback for browsers that
 * block storage. How the two stay in step: lib/analytics/account-consent.ts.
 */

// ─── The stored choice ──────────────────────────────────────────────────────

const COOKIE_CONSENT_KEY = 'alumnia-cookie-consent'
const COOKIE_PREFERENCES_KEY = 'alumnia-cookie-preferences'

export interface CookiePreferences {
  essential: true // Always true, required
  functional: boolean
  analytics: boolean
}

/** Before any choice. Analytics starts off: a pre-ticked box is not consent. */
export const DEFAULT_COOKIE_PREFERENCES: CookiePreferences = {
  essential: true,
  functional: true,
  analytics: false,
}

// Session-scoped fallback for users whose browser blocks localStorage
// (Safari + "Block all cookies", sandboxed iframes). Without this, every
// banner dismissal is a no-op and the banner reappears on every render.
let inMemoryConsent: boolean | null = null
let inMemoryPreferences: CookiePreferences | null = null

/** `true` once the visitor answered the banner (any answer), `null` before. */
export function getCookieConsent(): boolean | null {
  const consent = safeGetItem(COOKIE_CONSENT_KEY)
  if (!consent) return inMemoryConsent
  try {
    return JSON.parse(consent)
  } catch {
    return inMemoryConsent
  }
}

export function getCookiePreferences(): CookiePreferences {
  const prefs = safeGetItem(COOKIE_PREFERENCES_KEY)
  if (!prefs) return inMemoryPreferences ?? DEFAULT_COOKIE_PREFERENCES
  try {
    return { ...DEFAULT_COOKIE_PREFERENCES, ...JSON.parse(prefs) }
  } catch {
    return inMemoryPreferences ?? DEFAULT_COOKIE_PREFERENCES
  }
}

/** Stores an answer to the banner. */
export function storeCookieChoice(prefs: CookiePreferences): void {
  inMemoryConsent = true
  inMemoryPreferences = prefs
  safeSetItem(COOKIE_CONSENT_KEY, JSON.stringify(true))
  safeSetItem(COOKIE_PREFERENCES_KEY, JSON.stringify(prefs))
}

/** Forgets the answer so the banner asks again (preferences stay as the banner's starting point). */
export function clearCookieChoice(): void {
  inMemoryConsent = null
  inMemoryPreferences = null
  safeRemoveItem(COOKIE_CONSENT_KEY)
}

// ─── The mode ───────────────────────────────────────────────────────────────

export type AnalyticsMode = 'anonymous' | 'full'

/** This browser's banner answer. */
export interface BannerAnswer {
  answered: boolean
  analytics: boolean
}

export function bannerAnswer(): BannerAnswer {
  return {
    answered: getCookieConsent() === true,
    analytics: getCookiePreferences().analytics === true,
  }
}

/**
 * Turns this browser's stored banner answer to "no analytics" when it was a
 * yes (the account said no). Leaves an unanswered banner unanswered.
 */
export function declineAnalyticsOnThisBrowser(): void {
  const answer = bannerAnswer()
  if (answer.answered && answer.analytics) {
    storeCookieChoice({ ...getCookiePreferences(), analytics: false })
  }
}

/**
 * The rule. `account` is the signed-in member's answer, `null` when signed out.
 *
 * - The account decides when it has an answer: `granted` is full, `withdrawn`
 *   is anonymous, on the web and in the app, whatever this browser's banner says.
 * - Signed out, or signed in with no answer that counts (`none`): the web
 *   follows this browser's banner answer; the app, which has no banner, stays
 *   anonymous. (A web banner yes found while signed in with `none` is then
 *   recorded as the account's yes, lib/analytics/account-consent.ts.)
 */
export function resolveAnalyticsMode(input: {
  native: boolean
  account: AccountAnalyticsConsent | null
  banner: BannerAnswer
}): AnalyticsMode {
  if (input.account === 'granted') return 'full'
  if (input.account === 'withdrawn') return 'anonymous'
  if (input.native) return 'anonymous'
  return input.banner.answered && input.banner.analytics ? 'full' : 'anonymous'
}

/**
 * The account's last known answer on this browser, so a page load starts in
 * the mode it will end up in (no switch, no new ids on every load). Written
 * once the account's answer is read, cleared on sign-out; the fresh answer
 * always replaces it a moment after load.
 */
const ACCOUNT_CONSENT_KEY = 'alumnia-analytics-account'

export interface CachedAccountConsent {
  userId: string
  consent: AccountAnalyticsConsent
}

let inMemoryAccountConsent: CachedAccountConsent | null = null

export function cachedAccountConsent(): CachedAccountConsent | null {
  const raw = safeGetItem(ACCOUNT_CONSENT_KEY)
  if (!raw) return inMemoryAccountConsent
  try {
    const parsed = JSON.parse(raw) as Partial<CachedAccountConsent>
    if (
      typeof parsed.userId === 'string' &&
      (parsed.consent === 'granted' || parsed.consent === 'withdrawn' || parsed.consent === 'none')
    ) {
      return { userId: parsed.userId, consent: parsed.consent }
    }
  } catch {
    /* unreadable: treated as absent */
  }
  return inMemoryAccountConsent
}

export function cacheAccountConsent(userId: string, consent: AccountAnalyticsConsent): void {
  inMemoryAccountConsent = { userId, consent }
  safeSetItem(ACCOUNT_CONSENT_KEY, JSON.stringify(inMemoryAccountConsent))
}

export function clearAccountConsentCache(): void {
  inMemoryAccountConsent = null
  safeRemoveItem(ACCOUNT_CONSENT_KEY)
}

/**
 * The mode a page load starts in: the account's last known answer when there
 * is one, else the banner's. `native: false` is safe here (and avoids caching
 * a platform check made before the Capacitor bridge exists): the app never
 * stores a banner answer, so with no account answer it starts anonymous anyway.
 */
export function storedAnalyticsMode(): AnalyticsMode {
  return resolveAnalyticsMode({
    native: false,
    account: cachedAccountConsent()?.consent ?? null,
    banner: bannerAnswer(),
  })
}

/**
 * PostHog settings that differ between the modes. `init` takes the anonymous
 * or full half; `setAnalyticsMode` switches with `set_config`. Full is what
 * the SDK does by default with `defaults: '2026-01-30'` (what ran before this
 * split), written out so switching back restores it.
 */
export const POSTHOG_MODE_CONFIG = {
  anonymous: {
    persistence: 'memory',
    disable_session_recording: true,
    autocapture: false,
    rageclick: false,
    capture_dead_clicks: false,
    disable_surveys: true,
  },
  full: {
    persistence: 'localStorage+cookie',
    disable_session_recording: false,
    autocapture: true,
    rageclick: { content_ignorelist: true },
    // `capture_dead_clicks` has no client value in full mode (PostHog's
    // project setting decides), and `set_config` cannot unset a key, so the
    // anonymous `false` is lifted in `setAnalyticsMode`.
    disable_surveys: false,
  },
} satisfies Record<AnalyticsMode, Partial<PostHogConfig>>

/**
 * Events dropped in anonymous mode whatever PostHog's project settings turn on
 * remotely: element text (autocapture, rage/dead clicks, heatmaps) can be a
 * person's name, a replay is the whole screen, and person/alias events tie
 * the visitor to someone.
 */
const EVENTS_NEVER_ANONYMOUS = new Set([
  '$autocapture',
  '$rageclick',
  '$dead_click',
  '$$heatmap',
  '$snapshot',
  '$identify',
  '$create_alias',
  '$set',
])

/**
 * What `before_send` lets through in each mode. Full: everything. Anonymous:
 * none of the events above, and the rest without a person profile. Without
 * that flag the school group (`posthog.group`) would make PostHog create a
 * throwaway person for every anonymous page load.
 */
export function eventForMode<E extends { event: string; properties: Record<string, unknown> }>(
  mode: AnalyticsMode,
  event: E
): E | null {
  if (mode === 'full') return event
  if (EVENTS_NEVER_ANONYMOUS.has(event.event)) return null
  return { ...event, properties: { ...event.properties, $process_person_profile: false } }
}

// ─── Runtime state ──────────────────────────────────────────────────────────

let currentMode: AnalyticsMode = 'anonymous'
const listeners = new Set<(mode: AnalyticsMode) => void>()

export function getAnalyticsMode(): AnalyticsMode {
  return currentMode
}

/** Called with the new mode after every switch. Returns the unsubscribe. */
export function onAnalyticsModeChange(listener: (mode: AnalyticsMode) => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/**
 * Which mode the page load started in. Called by `instrumentation-client.ts`
 * right before `posthog.init`; in anonymous mode it also wipes what PostHog
 * stored in this browser before this change (or before a reject), so nothing
 * of it is read, sent or left behind.
 */
export function startAnalyticsMode(mode: AnalyticsMode): void {
  currentMode = mode
  writeModeCookie(mode)
  if (mode === 'anonymous') {
    clearPostHogBrowserState()
    clearAcquisitionTouch()
  }
}

/**
 * Switches mid-session (a banner answer, the Settings switch, the account's
 * answer arriving after load). Going full rotates the
 * anonymous id first, so what was captured before consent stays unlinked from
 * the person; going anonymous forgets the person and wipes the storage. Super
 * properties (app, school, role) survive both ways.
 */
export function setAnalyticsMode(ph: PostHog, mode: AnalyticsMode): void {
  if (mode === currentMode) return
  currentMode = mode

  if (ph.__loaded) {
    const carried = superProperties(ph)
    // New ids, empty storage; also drops any opt-out PostHog kept from before.
    ph.reset(true)
    if (Object.keys(carried).length > 0) ph.register(carried)
    if (mode === 'full') delete (ph.config as Partial<PostHogConfig>).capture_dead_clicks
    ph.set_config(POSTHOG_MODE_CONFIG[mode])
  }
  writeModeCookie(mode)
  if (mode === 'anonymous') {
    clearPostHogBrowserState()
    clearAcquisitionTouch()
  }

  for (const listener of listeners) listener(mode)
}

/**
 * First-party cookie that tells the server the mode (the choice lives in
 * localStorage, which the server can't read). Only `full` is written; its
 * absence means anonymous. Read by `lib/analytics/server-capture.ts`, which
 * attributes a server-side event to the visitor only with it.
 */
export const ANALYTICS_MODE_COOKIE = 'alumnia-analytics'

function writeModeCookie(mode: AnalyticsMode): void {
  if (typeof document === 'undefined') return
  try {
    const secure = window.location.protocol === 'https:' ? '; Secure' : ''
    document.cookie =
      mode === 'full'
        ? `${ANALYTICS_MODE_COOKIE}=full; path=/; max-age=31536000; SameSite=Lax${secure}`
        : `${ANALYTICS_MODE_COOKIE}=; path=/; max-age=0; SameSite=Lax${secure}`
  } catch {
    /* cookies blocked: the server treats the visitor as anonymous */
  }
}

/** Unprefixed keys posthog-js keeps next to the super properties. */
const POSTHOG_OWN_KEYS = new Set(['distinct_id', 'token'])

/** Our registered super properties plus the school group, without PostHog's ids. */
function superProperties(ph: PostHog): Record<string, unknown> {
  const carried: Record<string, unknown> = {}
  const props = ph.persistence?.properties() ?? {}
  for (const [key, value] of Object.entries(props)) {
    if (!key.startsWith('$') && !POSTHOG_OWN_KEYS.has(key)) carried[key] = value
  }
  const groups = ph.get_property('$groups')
  if (groups && typeof groups === 'object') carried.$groups = groups
  return carried
}

/**
 * Removes every trace posthog-js keeps in this browser: the `ph_*` cookie
 * (on this host and on the parent domains it may have been shared with), the
 * `ph_*` and `__ph_opt_in_out_*` localStorage entries and the `ph_*`
 * sessionStorage ones. Blocked storage is skipped silently.
 */
export function clearPostHogBrowserState(): void {
  if (typeof window === 'undefined') return
  for (const storage of ['localStorage', 'sessionStorage'] as const) {
    try {
      const store = window[storage]
      const keys: string[] = []
      for (let i = 0; i < store.length; i++) {
        const key = store.key(i)
        if (key && (key.startsWith('ph_') || key.startsWith('__ph_'))) keys.push(key)
      }
      for (const key of keys) store.removeItem(key)
    } catch {
      /* storage blocked: nothing stored there either */
    }
  }
  try {
    const names = document.cookie
      .split(';')
      .map((c) => c.split('=')[0]?.trim())
      .filter((name): name is string => !!name && name.startsWith('ph_'))
    const host = window.location.hostname
    const parts = host.split('.')
    const domains = [''].concat(
      parts.slice(0, -1).map((_, i) => `; domain=.${parts.slice(i).join('.')}`)
    )
    for (const name of names) {
      for (const domain of domains) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain}`
      }
    }
  } catch {
    /* cookies blocked */
  }
}
